import { z } from "zod";

import { COUNTRIES, COUNTRY_LABELS, type CountryCode } from "./countries";
import { CURRENCIES, emailSchema, type AccessLevel, type AuditChange } from "./organizations";
import {
	contractForReader,
	contractStanding,
	currentContract,
	PERSON_CONTRACTS_MAX,
	personContractSchema,
	type Pay,
	type PersonContract,
} from "./person-contracts";

// People & HR: the people an organization employs or contracts, the roles and teams it gives them, and the shapes of
// the requests that list and add them. The API and the frontend share these.

/** What someone is to the organization. Where they stand today also depends on their dates (`personStatus`). */
export const PERSON_TYPES = ["employee", "contractor"] as const;
export type PersonType = (typeof PERSON_TYPES)[number];

/** Form labels of the types; the audit log uses them to say what a person was added as. */
export const PERSON_TYPE_LABELS: Readonly<Record<PersonType, string>> = {
	employee: "Employee",
	contractor: "Contractor",
};

/**
 * Where a person stands, as the People & HR list shows it: what they are, unless their dates put them before their
 * first day or past their last one. It is never stored — `personStatus` works it out — so it can never go stale.
 */
export const PERSON_STATUSES = ["employee", "contractor", "starting", "ended"] as const;
export type PersonStatus = (typeof PERSON_STATUSES)[number];

/** What the Status column calls each status. */
export const PERSON_STATUS_LABELS: Readonly<Record<PersonStatus, string>> = {
	employee: "Employee",
	contractor: "Contractor",
	starting: "Starting",
	ended: "Ended",
};

/** A calendar date as the API keeps it, `YYYY-MM-DD`: a person's dates carry no time and no time zone. */
export const calendarDateSchema = z.iso.date();

/** The calendar day in UTC, which is the day the API and the page both judge a person's status by. */
export function utcToday(now: Date = new Date()): string {
	return now.toISOString().slice(0, 10);
}

/** A role or a team as someone types it: without surrounding spaces, at most 60 characters. */
export const personLabelSchema = z.string().trim().min(1, "This is required").max(60, "Use at most 60 characters");

/**
 * The key an organization keeps a role or a team under: the label in lowercase with runs of spaces collapsed, so
 * "Finance  Lead", "Finance Lead" and "finance lead" are one role. The first spelling given is the one kept.
 */
export function personLabelKey(label: string): string {
	return label.trim().replaceAll(/\s+/gu, " ").toLowerCase();
}

/**
 * The longest note a person's record carries. Notes ride along on every person the list reads, so this is a deliberate
 * ceiling rather than a formality: a page of context per person, not a document.
 */
export const PERSON_NOTE_MAX_LENGTH = 4000;

/**
 * What someone has written about a person, as Markdown. It is stored exactly as the editor produced it and is never
 * rendered as HTML by the API.
 */
export const personNoteSchema = z.string().max(PERSON_NOTE_MAX_LENGTH, "Use at most 4000 characters");

/** A phone number as someone types it: without surrounding spaces, at most 40 characters, in whatever format. */
export const personPhoneSchema = z.string().trim().min(1, "Enter a phone number").max(40, "Use at most 40 characters");

/** Where a person lives, as one line or several, the way an employment contract names it. */
export const homeAddressSchema = z.string().trim().min(1, "Enter an address").max(200, "Use at most 200 characters");

/**
 * Whom to call if something happens to a person at work: somebody else's name and number, kept for that alone. How
 * they are related to the person ("Partner", "Father") is optional.
 */
export const emergencyContactSchema = z.object({
	name: z.string().trim().min(1, "Enter a name").max(120, "Use at most 120 characters"),
	phone: personPhoneSchema,
	relationship: z.string().trim().min(1, "Enter a relationship").max(60, "Use at most 60 characters").optional(),
});
export type EmergencyContact = z.infer<typeof emergencyContactSchema>;

export const personSchema = z.object({
	id: z.string().min(1),
	/** Their full name, which is also what the list is ordered by. */
	name: z.string().min(1).max(80),
	email: z.email(),
	/** The number to reach them on at work; absent while nobody has given one. */
	workPhone: personPhoneSchema.optional(),
	/** Where they are, as an ISO 3166-1 alpha-2 code; absent while nobody has chosen one. Shown by its label. */
	country: z.enum(COUNTRIES).optional(),
	dateOfBirth: calendarDateSchema,
	/** Their first day. A person whose first day has not come is "starting". */
	startDate: calendarDateSchema,
	/** Their last day, inclusive; absent while nobody has said when they leave, which the page calls "Unspecified". */
	endDate: calendarDateSchema.optional(),
	type: z.enum(PERSON_TYPES),
	role: z.string().min(1).max(60),
	team: z.string().min(1).max(60),
	/**
	 * Where they live; absent while nobody has given it, and in whatever a reader of People & HR is sent
	 * (`personForReader`), since only an editor ever sees it.
	 */
	homeAddress: homeAddressSchema.optional(),
	/** Whom to call if something happens to them; absent, and kept from readers, the same way as the address. */
	emergencyContact: emergencyContactSchema.optional(),
	/** What someone has written about them, as Markdown; absent while nobody has written anything. */
	note: personNoteSchema.optional(),
	/**
	 * Their employment contracts and contractor agreements, each in effect from its own day (`./person-contracts.ts`),
	 * in the order they were added; absent until the first is added. Only the three contract routes write them: a
	 * change to the person writes them back as they were, and what they pay is kept from a reader.
	 */
	contracts: z.array(personContractSchema).max(PERSON_CONTRACTS_MAX).optional(),
	/** When the record was added, as an ISO 8601 timestamp in UTC. */
	addedAt: z.iso.datetime(),
});
export type Person = z.infer<typeof personSchema>;

/**
 * Where `person` stands on `today` (a `YYYY-MM-DD` date): "ended" once their last day has passed, "starting" until
 * their first day has come, and what they are on every day in between. Both dates count as days they are there.
 */
export function personStatus(
	person: Pick<Person, "type" | "startDate" | "endDate">,
	today: string = utcToday(),
): PersonStatus {
	if (person.endDate !== undefined && person.endDate < today) {
		return "ended";
	}
	return person.startDate > today ? "starting" : person.type;
}

/** The fields of a person that a request carries, trimmed and lowercased as the form sends them. */
const personFieldsSchema = z.object({
	name: z.string().trim().min(1, "Full name is required").max(80, "Use at most 80 characters"),
	email: emailSchema,
	/** Left out when nobody has given one, as are the address and the emergency contact below. */
	workPhone: personPhoneSchema.optional(),
	/** The form leaves it out when nobody has chosen one, exactly as it leaves out an end date nobody has given. */
	country: z.enum(COUNTRIES).optional(),
	dateOfBirth: calendarDateSchema,
	startDate: calendarDateSchema,
	endDate: calendarDateSchema.optional(),
	type: z.enum(PERSON_TYPES),
	role: personLabelSchema,
	team: personLabelSchema,
	homeAddress: homeAddressSchema.optional(),
	emergencyContact: emergencyContactSchema.optional(),
});

/** An end date before the start date is the one rule that spans two fields, so both requests refine on it. */
const endDateNotBeforeStart = {
	path: ["endDate"],
	message: "The end date is before the start date",
};

/** What the People & HR form sends to add a person. An end date nobody knows yet is left out. */
export const addPersonRequestSchema = personFieldsSchema.refine(
	person => person.endDate === undefined || person.endDate >= person.startDate,
	endDateNotBeforeStart,
);
export type AddPersonRequest = z.infer<typeof addPersonRequestSchema>;

/**
 * What the person page sends to change a person: every field of their record except the two the API owns (`id` and
 * `addedAt`), plus their note. A note sent empty clears it, which is why it is a field the request always carries
 * rather than one it leaves out.
 */
export const updatePersonRequestSchema = personFieldsSchema
	.extend({ note: personNoteSchema.optional() })
	.refine(person => person.endDate === undefined || person.endDate >= person.startDate, endDateNotBeforeStart);
export type UpdatePersonRequest = z.infer<typeof updatePersonRequestSchema>;

/**
 * The record an add or a change leaves behind: the id and the day they were added are the API's, and everything else is
 * what the request carried — a note of nothing but spaces no note at all. The two write handlers and the frontend's
 * fake API all build a person this way, so none of the three can drift from the others. A parsed request carries no
 * key for a field nobody gave, so spreading it never writes an undefined attribute, which the DynamoDB document client
 * refuses.
 */
export function personFrom(id: string, addedAt: string, request: UpdatePersonRequest): Person {
	const { note, ...fields } = request;
	const written = note?.trim();
	return { id, ...fields, ...(written !== undefined && written !== "" && { note: written }), addedAt };
}

/**
 * A person as a reader of People & HR is sent them: everything but where they live, whom to call in an emergency and
 * what their contracts pay, which are private details only an editor sees. A reader cannot save a person or a
 * contract, so nothing they are sent is ever written back without these.
 */
export function personForReader(person: Person): Person {
	const { homeAddress: _homeAddress, emergencyContact: _emergencyContact, contracts, ...seen } = person;
	return { ...seen, ...(contracts !== undefined && { contracts: contracts.map(contractForReader) }) };
}

/**
 * A person as a member with `access` to People & HR is sent them: whole to an editor, `personForReader` to anyone
 * else. Every route a reader may call that answers with a person, and the frontend's fake API, answers through this
 * (a page through `peoplePageAsSeenBy`), so the rule lives in one place; the routes that add or change a person are
 * for editors alone and answer the whole record.
 */
export function personAsSeenBy(person: Person, access: AccessLevel): Person {
	return access === "editor" ? person : personForReader(person);
}

/** The roles and the teams an organization has given its people, each once, A→Z. */
export const rolesAndTeamsSchema = z.object({
	roles: z.array(personSchema.shape.role),
	teams: z.array(personSchema.shape.team),
});
export type RolesAndTeams = z.infer<typeof rolesAndTeamsSchema>;

/** People & HR comes a page of this many people at a time, A→Z by name. */
export const PEOPLE_PAGE_SIZE = 10;

/** A link to another page of People & HR. The API makes it; a client passes it back unchanged and never reads it. */
export const peoplePageTokenSchema = z.string().regex(/^[\w-]{1,512}$/u, "Invalid page");

/** The longest search People & HR's list takes: as long as a person's name can be. */
export const PEOPLE_QUERY_MAX_LENGTH = 80;

/**
 * The query of `GET /api/people`: the page to show, the first when there is none, and what to narrow the list to — the
 * one team or role the page's two donuts name, one status, and a search of names — everyone when it names none
 * (`peopleMatching`).
 */
export const peopleQuerySchema = z.object({
	page: peoplePageTokenSchema.optional(),
	team: personLabelSchema.optional(),
	role: personLabelSchema.optional(),
	status: z.enum(PERSON_STATUSES).optional(),
	q: z.string().trim().max(PEOPLE_QUERY_MAX_LENGTH).optional(),
});
export type PeopleQuery = z.infer<typeof peopleQuerySchema>;

/** What a list of people can be narrowed to: everything its query takes but the page. */
export type PeopleFilter = Omit<PeopleQuery, "page">;

/** How many of an organization's people are of each status today. */
export const personCountsSchema = z.record(z.enum(PERSON_STATUSES), z.int().nonnegative());
export type PersonCounts = z.infer<typeof personCountsSchema>;

/** One group in a distribution: a team or a role, and how many people hold it. */
export const personGroupSchema = z.object({
	label: z.string().min(1).max(60),
	count: z.int().positive(),
});
export type PersonGroup = z.infer<typeof personGroupSchema>;

/**
 * How an organization's people divide between teams and between roles: the largest group first, and A→Z within one
 * size. People whose last day has passed are left out, so this answers who is on the books rather than who ever was.
 */
export const peopleDistributionSchema = z.object({
	teams: z.array(personGroupSchema),
	roles: z.array(personGroupSchema),
	/** How many people both lists count: everyone but those who have left. */
	total: z.int().nonnegative(),
});
export type PeopleDistribution = z.infer<typeof peopleDistributionSchema>;

/**
 * Counts `people` by one of their labels, largest group first and A→Z within a size. Two spellings of one label fold
 * together under `personLabelKey`, as an organization's roles and teams do, and the group keeps the spelling of the
 * first person who holds it — people reach here A→Z by name, so that is settled and stable.
 */
function groupByLabel(people: readonly Person[], labelOf: (person: Person) => string): PersonGroup[] {
	const groups: PersonGroup[] = [];
	const indexByKey = new Map<string, number>();
	for (const person of people) {
		const label = labelOf(person);
		const key = personLabelKey(label);
		const index = indexByKey.get(key);
		const group = index === undefined ? undefined : groups[index];
		if (group === undefined) {
			indexByKey.set(key, groups.length);
			groups.push({ label, count: 1 });
		} else {
			group.count += 1;
		}
	}
	return groups.toSorted((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * How an organization's people divide between teams and between roles on `today`, leaving out everyone whose last day
 * has passed. Shared, so the API, the local server and the frontend's fake all count the same way.
 */
export function peopleDistribution(people: readonly Person[], today: string = utcToday()): PeopleDistribution {
	const present = people.filter(person => personStatus(person, today) !== "ended");
	return {
		teams: groupByLabel(present, person => person.team),
		roles: groupByLabel(present, person => person.role),
		total: present.length,
	};
}

/**
 * The people `filter` keeps, in the order given; everyone when it names nothing. A team or a role keeps exactly who the
 * distribution counts in it — whoever holds it, spelled any way (`personLabelKey`), and has not left — so a list
 * narrowed to one of its groups holds as many people as the group says; a status keeps the people of that status
 * today, those who have left included when it says "ended"; and a search keeps the people whose name holds it, without
 * regard to case. Shared, so the API and the frontend's fake narrow the same way.
 */
export function peopleMatching(
	people: readonly Person[],
	{ team, role, status, q }: PeopleFilter,
	today: string = utcToday(),
): Person[] {
	const search = (q ?? "").trim().toLowerCase();
	if (team === undefined && role === undefined && status === undefined && search === "") {
		return [...people];
	}
	const teamKey = team === undefined ? undefined : personLabelKey(team);
	const roleKey = role === undefined ? undefined : personLabelKey(role);
	// A team or a role on its own keeps who its slice counts, which leaves out whoever has left; a status says outright
	// which people it keeps.
	const keepsStanding =
		status === undefined
			? (standing: PersonStatus) => (teamKey === undefined && roleKey === undefined) || standing !== "ended"
			: (standing: PersonStatus) => standing === status;
	return people.filter(
		person =>
			keepsStanding(personStatus(person, today)) &&
			(teamKey === undefined || personLabelKey(person.team) === teamKey) &&
			(roleKey === undefined || personLabelKey(person.role) === roleKey) &&
			person.name.toLowerCase().includes(search),
	);
}

/** How far ahead People & HR looks for what is coming up, in days. */
export const PEOPLE_UPCOMING_DAYS = 30;

/** What a month of pay comes to in one currency. */
export const monthlyPayrollSchema = z.object({
	currency: z.enum(CURRENCIES),
	/** Rounded to cents. */
	amount: z.number().nonnegative(),
});
export type MonthlyPayroll = z.infer<typeof monthlyPayrollSchema>;

/**
 * What the organization pays the people on its books each month, worked out from the contract each of them has in
 * effect today: a total for each currency they are paid in, largest first — never converted: People & HR does not use
 * the ECB's reference rates (docs/exchange-rates.md) — and how many of them no monthly figure can hold.
 */
export const peoplePayrollSchema = z.object({
	monthly: z.array(monthlyPayrollSchema),
	/** People paid by the hour or the day, whose month depends on how much they work. */
	byTheHourOrDay: z.int().nonnegative(),
	/** People on the books with no contract in effect today, so nothing to count. */
	withoutContract: z.int().nonnegative(),
});
export type PeoplePayroll = z.infer<typeof peoplePayrollSchema>;

/**
 * What comes up for an organization's people from today to `PEOPLE_UPCOMING_DAYS` days on, both days included — except
 * a first day, which counts from tomorrow: somebody starting today is already on the books.
 */
export const peopleUpcomingSchema = z.object({
	/** People whose first day comes after today. */
	starting: z.int().nonnegative(),
	/** People whose last day comes. */
	leaving: z.int().nonnegative(),
	/** People whose probation, in the contract in effect, ends while they and that contract are still there. */
	probationsEnding: z.int().nonnegative(),
	/**
	 * People whose contract in effect ends with no newer one to follow it and no last day of their own by then: a
	 * contract somebody must renew or let run out.
	 */
	contractsEnding: z.int().nonnegative(),
});
export type PeopleUpcoming = z.infer<typeof peopleUpcomingSchema>;

/** The calendar day `days` after `date`, both `YYYY-MM-DD`. */
function daysAfter(date: string, days: number): string {
	const moment = new Date(`${date}T00:00:00.000Z`);
	moment.setUTCDate(moment.getUTCDate() + days);
	return moment.toISOString().slice(0, 10);
}

/** What one month of `pay` comes to, or undefined for pay by the hour or the day, which depends on the work done. */
function monthOf(pay: Pay): number | undefined {
	if (pay.unit === "month") {
		return pay.amount;
	}
	return pay.unit === "year" ? pay.amount / 12 : undefined;
}

/**
 * What the people on the books on `today` — employees and contractors, not those starting later or already gone — are
 * paid each month, from the contract each has in effect. Shared, so the API and the frontend's fake add up the same.
 */
export function peoplePayroll(people: readonly Person[], today: string = utcToday()): PeoplePayroll {
	const monthly: MonthlyPayroll[] = [];
	let byTheHourOrDay = 0;
	let withoutContract = 0;
	for (const person of people) {
		const status = personStatus(person, today);
		const contract = currentContract(person.contracts ?? [], today);
		const inEffect = contract !== undefined && contractStanding(contract, person, today) === "current";
		const month = inEffect && contract.pay !== undefined ? monthOf(contract.pay) : undefined;
		if (status === "starting" || status === "ended") {
			// Not on the books today, so not on this month's payroll either.
		} else if (!inEffect || contract.pay === undefined) {
			withoutContract += 1;
		} else if (month === undefined) {
			byTheHourOrDay += 1;
		} else {
			const total = monthly.find(entry => entry.currency === contract.pay?.currency);
			if (total === undefined) {
				monthly.push({ currency: contract.pay.currency, amount: month });
			} else {
				total.amount += month;
			}
		}
	}
	return {
		monthly: monthly
			.map(entry => ({ currency: entry.currency, amount: Math.round(entry.amount * 100) / 100 }))
			.toSorted((a, b) => b.amount - a.amount || a.currency.localeCompare(b.currency)),
		byTheHourOrDay,
		withoutContract,
	};
}

/**
 * The last day of `person`'s probation in `contract`, the one in effect, unless the contract or the person is gone by
 * then: somebody who leaves first, or whose contract ends first — or already has — asks for no decision about a
 * probation, and the leaving and the contract running out are counted on their own.
 */
function probationToDecide(person: Person, contract: PersonContract | undefined): string | undefined {
	if (contract?.kind !== "employment" || contract.probationUntil === undefined) {
		return undefined;
	}
	const { probationUntil } = contract;
	const goneFirst = [contract.endsOn, person.endDate].some(last => last !== undefined && last <= probationUntil);
	return goneFirst ? undefined : probationUntil;
}

/**
 * What comes up for `people` from `today` to `PEOPLE_UPCOMING_DAYS` days on: who starts and who leaves, whose probation
 * ends with them still there on that contract (`probationToDecide`), and whose contract runs out with nothing after it.
 * Shared, so the API and the frontend's fake count the same.
 */
export function peopleUpcoming(people: readonly Person[], today: string = utcToday()): PeopleUpcoming {
	const horizon = daysAfter(today, PEOPLE_UPCOMING_DAYS);
	const comesUp = (day: string | undefined) => day !== undefined && day >= today && day <= horizon;
	const upcoming: PeopleUpcoming = { starting: 0, leaving: 0, probationsEnding: 0, contractsEnding: 0 };
	for (const person of people) {
		const contracts = person.contracts ?? [];
		const contract = currentContract(contracts, today);
		const followed = contracts.some(other => other.effectiveFrom > today);
		const leavesFirst = person.endDate !== undefined && person.endDate <= (contract?.endsOn ?? person.endDate);
		if (person.startDate > today && comesUp(person.startDate)) {
			upcoming.starting += 1;
		}
		if (comesUp(person.endDate)) {
			upcoming.leaving += 1;
		}
		if (comesUp(probationToDecide(person, contract))) {
			upcoming.probationsEnding += 1;
		}
		if (!followed && !leavesFirst && comesUp(contract?.endsOn)) {
			upcoming.contractsEnding += 1;
		}
	}
	return upcoming;
}

/** One page of an organization's people, A→Z by name, with links to the pages beside it: `null` where there is none. */
export const peoplePageSchema = z.object({
	people: z.array(personSchema).max(PEOPLE_PAGE_SIZE),
	/** How many people the list pages through: everyone, or the people of the one team or role the query named. */
	total: z.int().nonnegative(),
	/** Every person of the organization counted by status, whichever page this is. */
	counts: personCountsSchema,
	/** How every person of the organization divides between teams and roles, whichever page this is. */
	distribution: peopleDistributionSchema,
	/** What every person on the books is paid a month; absent from what a reader is sent, since only an editor sees pay. */
	payroll: peoplePayrollSchema.optional(),
	/** What comes up for every person of the organization in the next `PEOPLE_UPCOMING_DAYS` days. */
	upcoming: peopleUpcomingSchema,
	/** Further down the alphabet. */
	next: peoplePageTokenSchema.nullable(),
	/** Back up the alphabet. */
	previous: peoplePageTokenSchema.nullable(),
});
export type PeoplePage = z.infer<typeof peoplePageSchema>;

/**
 * A page of People & HR as a member with `access` to it is sent it: whole to an editor; to anyone else, every person as
 * `personForReader` has them and no payroll. The list route and the frontend's fake answer through this.
 */
export function peoplePageAsSeenBy(page: PeoplePage, access: AccessLevel): PeoplePage {
	const { payroll, people, ...rest } = page;
	return {
		...rest,
		people: people.map(person => personAsSeenBy(person, access)),
		...(access === "editor" && payroll !== undefined && { payroll }),
	};
}

/** What the audit log says instead of a date nobody has given yet. */
export const UNSPECIFIED_DATE = "Unspecified";

/**
 * What the audit log says instead of a value nobody has given — a country nobody chose, a work phone nobody entered —
 * the same word `UNSPECIFIED_VALUE` in `./parties.ts` uses for the same absence. It is a second declaration rather than
 * an import because `./parties.ts` imports from here and the seam only runs that way, and it is private because
 * `./index.ts` re-exports both modules and `import-x/export` refuses a name two `export *` lines carry. Move both into
 * a module of their own if a third one ever needs it.
 */
const UNSPECIFIED = "—";

/** What the audit log calls a person's country: its label, or `UNSPECIFIED` while they have none. */
function countryLabel(country: CountryCode | undefined): string {
	return country === undefined ? UNSPECIFIED : COUNTRY_LABELS[country];
}

/** An emergency contact as one text, to tell whether it changed: undefined while there is none. */
function emergencyContactText(contact: EmergencyContact | undefined): string | undefined {
	return contact === undefined ? undefined : [contact.name, contact.phone, contact.relationship ?? ""].join("\n");
}

/**
 * What the audit log says about a value it never quotes — a note, or a private detail — which is what became of it:
 * Added, Changed or Cleared, and nothing when it stayed the same.
 */
function describeUnquoted(before: string | undefined, after: string | undefined): string | undefined {
	if (before === after) {
		return undefined;
	}
	if (after === undefined) {
		return "Cleared";
	}
	return before === undefined ? "Added" : "Changed";
}

/**
 * A new person's values: everything they were added with, and nothing before it. The home address and the emergency
 * contact are private details, so they are recorded as Added when given and never quoted: the audit log outlives the
 * person, and an entry records that someone was told whom to call, not the number.
 */
export function describeNewPerson(person: Person): AuditChange[] {
	return [
		{ label: "Full name", value: person.name },
		{ label: "Email", value: person.email },
		{ label: "Work phone", value: person.workPhone ?? UNSPECIFIED },
		{ label: "Country", value: countryLabel(person.country) },
		{ label: "Date of birth", value: person.dateOfBirth },
		{ label: "Start date", value: person.startDate },
		{ label: "End date", value: person.endDate ?? UNSPECIFIED_DATE },
		{ label: "Type", value: PERSON_TYPE_LABELS[person.type] },
		{ label: "Role", value: person.role },
		{ label: "Team", value: person.team },
		{ label: "Home address", value: describeUnquoted(undefined, person.homeAddress) ?? UNSPECIFIED },
		{
			label: "Emergency contact",
			value: describeUnquoted(undefined, emergencyContactText(person.emergencyContact)) ?? UNSPECIFIED,
		},
	];
}

/**
 * The audit entry for a person added to People & HR: "Added person" / "Ida P. · Ops analyst · Operations". Mirrors
 * `documentAddedAudit` in `./intake.ts`.
 */
export function personAddedAudit(person: Person): { action: string; detail: string } {
	return { action: "Added person", detail: `${person.name} · ${person.role} · ${person.team}` };
}

/**
 * What changed between two versions of a person, newest value first, for the audit log. The home address, the
 * emergency contact and the note are reported as Added, Changed or Cleared and are never quoted: an entry records that
 * somebody wrote about a person, or changed whom to call, not what they wrote or the number. Mirrors
 * `describeMemberChanges` in `./organizations.ts`.
 */
export function describePersonChanges(before: Person, after: Person): AuditChange[] {
	const changes: AuditChange[] = [];
	const field = (label: string, previous: string, value: string) => {
		if (previous !== value) {
			changes.push({ label, value, previous });
		}
	};
	const unquoted = (label: string, previous: string | undefined, value: string | undefined) => {
		const became = describeUnquoted(previous, value);
		if (became !== undefined) {
			changes.push({ label, value: became });
		}
	};
	field("Full name", before.name, after.name);
	field("Email", before.email, after.email);
	field("Work phone", before.workPhone ?? UNSPECIFIED, after.workPhone ?? UNSPECIFIED);
	field("Country", countryLabel(before.country), countryLabel(after.country));
	field("Date of birth", before.dateOfBirth, after.dateOfBirth);
	field("Start date", before.startDate, after.startDate);
	field("End date", before.endDate ?? UNSPECIFIED_DATE, after.endDate ?? UNSPECIFIED_DATE);
	field("Type", PERSON_TYPE_LABELS[before.type], PERSON_TYPE_LABELS[after.type]);
	field("Role", before.role, after.role);
	field("Team", before.team, after.team);
	unquoted("Home address", before.homeAddress, after.homeAddress);
	unquoted(
		"Emergency contact",
		emergencyContactText(before.emergencyContact),
		emergencyContactText(after.emergencyContact),
	);
	unquoted("Note", before.note, after.note);
	return changes;
}

/** The audit entry for a person whose record changed: "Updated person" / "Ida P. · Role: Ops lead · Note: Changed". */
export function personUpdatedAudit(
	person: Person,
	changes: readonly AuditChange[],
): { action: string; detail: string } {
	return {
		action: "Updated person",
		detail: [person.name, ...changes.map(change => `${change.label}: ${change.value}`)].join(" · "),
	};
}

/** The audit entry for a person taken out of People & HR: "Removed person" / "Ida P. · Ops analyst · Operations". */
export function personRemovedAudit(person: Person): { action: string; detail: string } {
	return { action: "Removed person", detail: `${person.name} · ${person.role} · ${person.team}` };
}
